import { eligibleParent, type FollowerKind } from '../timing/followers'
import { configWindow } from '../timing/sceneSync'
import {
  renderConfigV2Schema,
  TIMELINE_CONFIG_VERSION,
  type MediaItemV2,
  type RenderConfigV2,
  type RenderConfigV3,
  type TimelineItemV2,
  type TimelineItemV3,
  type TrackV2,
  type TrackV3,
} from './renderConfigV2'
import { FPS } from './video'

/**
 * Lifting a generation-2 document to generation 3 (#541, #566). Pure.
 *
 * In generation 2 a recording is a video or audio item on a media track, and
 * its followers (captions, speaker texts and pictures, the visualizer) name
 * that TRACK. In generation 3 a recording is a `scene` on the one scenes
 * track, its picture a `view`, and a follower names the ITEM it reads in
 * `follows`. The lift maps one to the other without changing a pixel:
 *
 * - **Recordings become scenes.** In time order (the upper track first on a
 *   tie), each recording item that overlaps no scene placed before it becomes
 *   a scene with its id, span, window, volume and sound fade, on a new track
 *   at the bottom (a scene draws nothing, so its place in the paint order is
 *   moot). A video also gets one view, in the item's own place on its own
 *   track, so the picture paints exactly where it did; `lowerScenes` turns
 *   the pair back into the same video item, with the same id.
 * - **A recording that overlaps a placed scene stays a free item**, and so
 *   does every uploaded clip: generation 3 lets a follower read either.
 * - **A hidden or muted media track stays silent.** The scenes track is muted
 *   when every track its scenes came from was hidden or muted; otherwise each
 *   scene from such a track plays at volume 0. A view keeps its own track's
 *   flags (a hidden track was silent in generation 2: the layers skip it).
 * - **A track-bound follower becomes one follower per item it read** (any
 *   item from its track that `eligibleParent` accepts): captions one per
 *   recording, spanning it (derived); a speaker element or visualizer one per
 *   item it overlaps, clamped once to that item's span, so copies never
 *   overlap and a short label stays short. The first copy keeps the id. A
 *   follower left with nothing to read is dropped and reported.
 *
 * `template.layout` stays on the document; it is not copied onto scenes.
 */

export type LiftDrop = {
  itemId: string
  /** `component:<id>` for a component, else the item type. */
  type: string
  reason: string
}

export type LiftResult = {
  config: RenderConfigV3
  dropped: LiftDrop[]
  /** One plain line per kind of loss; empty when nothing was lost. */
  report: string[]
}

const EPSILON = 1e-6
const ITEM_ID_MAX = 64

type Span = { from: number; duration: number }

/** Whether two spans overlap by more than the validator's one-frame tolerance. */
function overlaps(a: Span, b: Span): boolean {
  const [first, second] = a.from <= b.from ? [a, b] : [b, a]
  return first.from + first.duration > second.from + 1 / FPS - EPSILON
}

const round3 = (n: number) => Math.round(n * 1000) / 1000

/**
 * A fresh id: `<base>-<suffix>`, kept within the schema's 64 characters by
 * shortening the base, and bumped past any id in `taken`. The chosen id is
 * added to `taken`, so successive calls never collide.
 */
function cloneId(base: string, suffix: string, taken: Set<string>): string {
  for (let k = 1; ; k++) {
    const tail = `-${suffix}${k === 1 ? '' : `-${k}`}`
    const head = base.slice(0, Math.max(1, ITEM_ID_MAX - tail.length))
    const id = `${head}${tail}`
    if (!taken.has(id)) {
      taken.add(id)
      return id
    }
  }
}

const isRecording = (item: TimelineItemV2): item is MediaItemV2 =>
  (item.type === 'video' || item.type === 'audio') &&
  item.source.kind === 'recording'

function boundTrack(item: TimelineItemV2): string | undefined {
  if (item.type === 'captions') return item.for
  if (item.type === 'text') return item.dynamic?.for
  if (item.type === 'component') return item.for
  return undefined
}

function kindOf(item: TimelineItemV2): FollowerKind | null {
  if (item.type === 'captions') return 'captions'
  if (item.type === 'text') return item.dynamic ? 'speaker' : null
  if (item.type === 'component') {
    if (item.componentId === 'audio-visualizer') return 'visualizer'
    if (item.componentId === 'picture' && item.props.source === 'speaker')
      return 'speaker'
  }
  return null
}

function typeOf(item: TimelineItemV2): string {
  return item.type === 'component' ? `component:${item.componentId}` : item.type
}

/** A generation-2 item with its track binding removed, as generation 3 spells it. */
function unbound(item: TimelineItemV2): TimelineItemV3 {
  if (item.type === 'captions') {
    const { for: _for, ...rest } = item
    void _for
    return rest as unknown as TimelineItemV3
  }
  if (item.type === 'text' && item.dynamic) {
    const { for: _for, ...dynamic } = item.dynamic
    void _for
    return { ...item, dynamic } as TimelineItemV3
  }
  if (item.type === 'component' && 'for' in item) {
    const { for: _for, ...rest } = item
    void _for
    return rest as TimelineItemV3
  }
  return item as TimelineItemV3
}

export function liftToScenes(v2: RenderConfigV2): LiftResult {
  const taken = new Set<string>()
  for (const track of v2.tracks) {
    taken.add(track.id)
    for (const item of track.items) taken.add(item.id)
  }

  // 1. Which recordings become scenes.
  const candidates = v2.tracks
    .flatMap((track, trackIndex) =>
      track.items
        .filter(isRecording)
        .map((item) => ({ item, track, trackIndex })),
    )
    .sort((a, b) => a.item.from - b.item.from || a.trackIndex - b.trackIndex)
  const placed: typeof candidates = []
  for (const candidate of candidates)
    if (!placed.some((p) => overlaps(p.item, candidate.item)))
      placed.push(candidate)
  const sceneOf = new Map(placed.map((p) => [p.item.id, p]))

  // 2. Hidden and muted media tracks stay silent.
  const silentTrack = (track: TrackV2) => !!(track.hidden || track.muted)
  const sourceTracks = new Set(placed.map((p) => p.track))
  const muteScenesTrack =
    sourceTracks.size > 0 && [...sourceTracks].every(silentTrack)

  const scenes: TimelineItemV3[] = placed
    .map(({ item, track }) => {
      const soundFade =
        item.type === 'video' ? (item.audioFade ?? item.fade) : item.fade
      const volume = !muteScenesTrack && silentTrack(track) ? 0 : item.volume
      return {
        id: item.id,
        type: 'scene' as const,
        from: item.from,
        duration: item.duration,
        ...(item.transitionIn ? { transitionIn: item.transitionIn } : {}),
        ...(item.type === 'audio' && item.picked
          ? { picked: item.picked }
          : {}),
        source: item.source,
        sourceStart: item.sourceStart,
        sourceEnd: item.sourceEnd ?? configWindow(item).end,
        ...(volume !== undefined ? { volume } : {}),
        ...(soundFade ? { fade: soundFade } : {}),
        ...(item.highlightId ? { highlightId: item.highlightId } : {}),
      }
    })
    .sort((a, b) => a.from - b.from)

  // 3. Every track: scenes' videos become views in place, their audio leaves,
  //    followers become one copy per item they read.
  const dropped: LiftDrop[] = []
  const parentsOnTrack = new Map<string, TimelineItemV3[]>()
  const liftedMedia = (item: TimelineItemV2): TimelineItemV3 | null => {
    if (sceneOf.has(item.id)) return scenes.find((s) => s.id === item.id)!
    if (item.type === 'video' || item.type === 'audio')
      return item as TimelineItemV3
    return null
  }
  for (const track of v2.tracks)
    parentsOnTrack.set(
      track.id,
      track.items.flatMap((item) => {
        const lifted = liftedMedia(item)
        return lifted ? [lifted] : []
      }),
    )

  const tracks: TrackV3[] = []
  for (const track of v2.tracks) {
    const items: TimelineItemV3[] = []
    for (const item of track.items) {
      const scene = sceneOf.get(item.id)
      if (scene) {
        if (item.type === 'video')
          items.push({
            id: cloneId(item.id, 'view', taken),
            type: 'view',
            scene: item.id,
            from: item.from,
            duration: item.duration,
            ...(item.picked ? { picked: item.picked } : {}),
            ...(item.transform ? { transform: item.transform } : {}),
            ...(item.fit ? { fit: item.fit } : {}),
            ...(item.backdrop ? { backdrop: item.backdrop } : {}),
            ...(item.fade ? { fade: item.fade } : {}),
          })
        continue
      }
      const kind = kindOf(item)
      const bound = boundTrack(item)
      if (!kind || bound === undefined) {
        items.push(unbound(item))
        continue
      }
      const parents = (parentsOnTrack.get(bound) ?? []).filter(
        (parent) =>
          eligibleParent(kind, parent) &&
          (kind === 'captions' || overlaps(parent, item)),
      )
      if (!parents.length) {
        dropped.push({
          itemId: item.id,
          type: typeOf(item),
          reason: `follows track "${bound}", which plays nothing it can read`,
        })
        continue
      }
      parents.forEach((parent, index) => {
        const copy = {
          ...unbound(item),
          follows: parent.id,
          id:
            index === 0 ? item.id : cloneId(item.id, String(index + 1), taken),
        } as TimelineItemV3
        if (kind === 'captions') {
          copy.from = parent.from
          copy.duration = parent.duration
        } else {
          const from = Math.max(parent.from, item.from)
          const end = Math.min(
            parent.from + parent.duration,
            item.from + item.duration,
          )
          copy.from = round3(from)
          copy.duration = round3(end - from)
          withinFades(copy)
        }
        items.push(copy)
      })
    }
    // A media track the lift emptied (an audiogram's) goes.
    if (track.items.length && !items.length) continue
    items.sort((a, b) => a.from - b.from)
    // Mute is the scene's now (the scenes track, or its volume): a track of
    // views alone sheds it, so its first view still carries the sound.
    const viewsOnly =
      items.some((i) => i.type === 'view') &&
      !items.some((i) => i.type === 'video' || i.type === 'audio')
    if (viewsOnly && track.muted) {
      const { muted: _muted, ...rest } = track
      void _muted
      tracks.push({ ...rest, items })
    } else tracks.push({ ...track, items })
  }

  if (scenes.length) {
    const id = taken.has('scenes')
      ? cloneId('scenes', 'track', taken)
      : 'scenes'
    tracks.push({
      id,
      items: scenes,
      ...(muteScenesTrack ? { muted: true } : {}),
    })
  }

  const config: RenderConfigV3 = {
    ...v2,
    version: TIMELINE_CONFIG_VERSION,
    tracks,
  }
  return { config, dropped, report: describeDrops(dropped) }
}

/** A clamped copy never fades for longer than it lasts. */
function withinFades(item: TimelineItemV3) {
  if (!('fade' in item) || !item.fade) return
  const fade = item.fade
  item.fade = {
    ...(fade.in !== undefined ? { in: Math.min(fade.in, item.duration) } : {}),
    ...(fade.out !== undefined
      ? { out: Math.min(fade.out, item.duration) }
      : {}),
  }
}

const NOUNS: Record<string, [string, string]> = {
  captions: ['captions', 'captions'],
  text: ['speaker label', 'speaker labels'],
  'component:picture': ['speaker picture', 'speaker pictures'],
  'component:audio-visualizer': ['visualizer', 'visualizers'],
}

/** "2 captions couldn't be kept", one line per kind. */
function describeDrops(dropped: LiftDrop[]): string[] {
  const counts = new Map<string, number>()
  for (const d of dropped) counts.set(d.type, (counts.get(d.type) ?? 0) + 1)
  return [...counts].map(([type, n]) => {
    const [one, many] = NOUNS[type] ?? ['element', 'elements']
    return n === 1
      ? `${type === 'captions' ? 'Captions' : `A ${one}`} couldn't be kept`
      : `${n} ${many} couldn't be kept`
  })
}

/**
 * Lift a stored document if it is a valid generation-2 one; anything else
 * (generation 3, v1, a damaged document salvage has not repaired) comes back
 * as it went in, for the caller's own rules to judge.
 */
export function liftRenderConfig(input: unknown): unknown {
  const parsed = renderConfigV2Schema.safeParse(input)
  if (!parsed.success) return input
  return liftToScenes(parsed.data).config
}
